import { useTranslation } from 'react-i18next';
import type { AgencyBrand } from '../../types';
import { Building2, Mail, Phone, Icon } from '../ui/Icon';

interface Props {
  agency: AgencyBrand;
  /** `compact` in the sidebar; full in the Info tab. */
  compact?: boolean;
}

/**
 * ADR-0033: who prepared the trip and who to call — the agency's card, shown
 * only while it runs the trip (the server sends it only then). One card, not an
 * app per agency.
 */
export default function AgencyCard({ agency, compact = false }: Props) {
  const { t } = useTranslation();
  return (
    <div className={`rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 ${compact ? 'px-3 py-2.5' : 'p-4 mb-5'}`}>
      <div className="flex items-center gap-3">
        {agency.logoUrl ? (
          <img src={agency.logoUrl} alt="" className={`${compact ? 'w-8 h-8' : 'w-11 h-11'} rounded-lg object-contain bg-white shrink-0`} />
        ) : (
          <div className={`${compact ? 'w-8 h-8' : 'w-11 h-11'} rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0`}>
            <Icon as={Building2} size={compact ? 15 : 18} className="text-slate-500" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400">{t('agency.card.preparedBy')}</p>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{agency.name}</p>
        </div>
      </div>
      {(agency.emergencyPhone || agency.contactEmail) && (
        <div className={`flex flex-col gap-1 ${compact ? 'mt-2' : 'mt-3'}`}>
          {agency.emergencyPhone && (
            <a href={`tel:${agency.emergencyPhone.replace(/\s/g, '')}`}
              className="flex items-center gap-2 text-[13px] text-teal-700 dark:text-teal-300 hover:underline"
              aria-label={`${t('agency.card.emergency')}: ${agency.emergencyPhone}`}>
              <Icon as={Phone} size={13} />
              <span className="truncate">{compact ? agency.emergencyPhone : `${t('agency.card.emergency')} · ${agency.emergencyPhone}`}</span>
            </a>
          )}
          {!compact && agency.contactEmail && (
            <a href={`mailto:${agency.contactEmail}`}
              className="flex items-center gap-2 text-[13px] text-teal-700 dark:text-teal-300 hover:underline">
              <Icon as={Mail} size={13} />
              <span className="truncate">{agency.contactEmail}</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
}
